import {
  CommonActions,
  NavigationContainer,
  NavigationContainerRef,
  ParamListBase,
  useNavigation,
} from '@react-navigation/native';
import React, { useEffect, useRef } from 'react';
import { BackHandler } from 'react-native';
import { GlobalInterval } from '../hooks/interval/GlobalInterval';
import {
  SubNavigatorName,
  useInitialScreen,
} from '../hooks/login/useInitialScreen';
import IdleScreen from '../screens/IdleScreen/IdleScreen';
import LoginScreen from '../screens/LoginScreen/LoginScreen';
import { CartNavigator } from './CartNavigator';
import PairingScreen from '../screens/PairingScreen/PairingScreen';
import { sessionSubject } from '../state/sessionObservable';
import { skip } from 'rxjs';
import { ModalProvider } from '@oolio-group/rn-use-modal';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { useDevices } from '../hooks/organization/useDevices';
import { useStoreSync } from '../hooks/organization/useStoreSync';
import { useOrgSync } from '../hooks/organization/useOrgSync';
import useBehaviorSubjectState from '../hooks/rxjs/useSubjectState';
import { useAppVersionSync } from '../hooks/useAppVersionSync';
import { usePrefetchDisplayMedia } from '../hooks/media/usePrefetchDisplayMedia';
import { useAutoRestart } from '../hooks/autoRestart/useAutoRestart';
import { isIdleRoute } from './isIdleRoute';

const Stack = createNativeStackNavigator();

const AutoRestartEffect: React.FC<{
  navigationRef: React.RefObject<NavigationContainerRef<ParamListBase>>;
}> = ({ navigationRef }) => {
  const check = useAutoRestart(() =>
    isIdleRoute(navigationRef.current?.getCurrentRoute()?.name),
  );

  useEffect(
    () => navigationRef.current?.addListener('state', () => void check()),
    [check, navigationRef],
  );
  return null;
};

export const MainNavigator: React.FC = () => {
  const navigation = useNavigation();
  const { screen } = useInitialScreen();
  const { refetch: refetchDevices } = useDevices();
  const { value: session } = useBehaviorSubjectState(sessionSubject);
  useStoreSync();
  useOrgSync();
  usePrefetchDisplayMedia();

  useEffect(() => {
    sessionSubject.pipe(skip(1)).subscribe(session => {
      if (!session?.authorized) {
        navigation.dispatch(
          CommonActions.reset({
            index: 0,
            routes: [{ name: SubNavigatorName.Login }],
          }),
        );
      }
    });
  }, [navigation]);

  // Fetch devices when app loads and user has a store selected
  useEffect(() => {
    if (session?.authorized && session?.currentStore?.id) {
      refetchDevices();
    }
  }, [session?.authorized, session?.currentStore?.id, refetchDevices]);

  useAppVersionSync();

  return screen === SubNavigatorName.Idle ? (
    <IdleScreen />
  ) : (
    <Stack.Navigator
      initialRouteName={screen}
      screenOptions={{ headerShown: false }}
    >
      <Stack.Screen component={LoginScreen} name={SubNavigatorName.Login} />
      <Stack.Screen component={PairingScreen} name={SubNavigatorName.Pairing} />
      <Stack.Screen component={CartNavigator} name={SubNavigatorName.Cart} />
    </Stack.Navigator>
  );
};

const AppNavigator = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigationRef = useRef<NavigationContainerRef<any>>(null);

  useEffect(() => {
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => true,
    );
    return () => subscription.remove();
  }, []);

  return (
    <NavigationContainer
      ref={navigationRef}
      documentTitle={{ formatter: () => 'Oolio CDS' }}
    >
      <ModalProvider>
        <GlobalInterval />
        <AutoRestartEffect navigationRef={navigationRef} />
        <MainNavigator />
      </ModalProvider>
    </NavigationContainer>
  );
};

export default AppNavigator;
